/**
 * 认证状态管理
 * 使用 @vue/reactivity 实现响应式状态
 */

import { ref, computed, type Ref, type ComputedRef } from '@vue/reactivity'
import type { IStorage } from '../storage/Storage'
import type { AuthSession, AuthState, UserInfo, QRCodeInfo } from '../../types/auth'
import { STORAGE_KEYS } from '../../constants/storage-keys'
import { BaseStore } from './BaseStore'
import { extractStoredAuthToken } from '../utils/authToken'

export class AuthStore extends BaseStore<AuthState> {
  // 响应式状态
  private _token: Ref<string | null>
  private _refreshToken: Ref<string | null>
  private _accessTokenExpiresAt: Ref<number | null>
  private _refreshTokenExpiresAt: Ref<number | null>
  private _sessionId: Ref<string | null>
  private _refreshVersion: Ref<number | null>
  private _userInfo: Ref<UserInfo | null>
  private _qrCodeInfo: Ref<QRCodeInfo | null>

  // Token 变化监听器
  private tokenChangeListeners: Array<(token: string | null) => void> = []

  // 计算属性
  public readonly isLogin: ComputedRef<boolean>
  public readonly hasToken: ComputedRef<boolean>

  constructor(storage: IStorage) {
    super(storage, STORAGE_KEYS.TOKEN)

    // 初始化响应式状态
    this._token = ref<string | null>(null)
    this._refreshToken = ref<string | null>(null)
    this._accessTokenExpiresAt = ref<number | null>(null)
    this._refreshTokenExpiresAt = ref<number | null>(null)
    this._sessionId = ref<string | null>(null)
    this._refreshVersion = ref<number | null>(null)
    this._userInfo = ref<UserInfo | null>(null)
    this._qrCodeInfo = ref<QRCodeInfo | null>(null)

    // 初始化计算属性
    // 只要有 token 就算登录成功（userInfo 可能需要单独获取）
    this.isLogin = computed(() => !!this._token.value)
    this.hasToken = computed(() => !!this._token.value)

    // 从存储中恢复状态
    this.hydrate()
  }

  /**
   * 从存储中恢复状态（兼容旧版 token key）
   */
  hydrate(): void {
    super.hydrate()

    if (this._token.value || typeof localStorage === 'undefined') return

    const legacyKeys = ['token', 'nano_token', 'csd_token']
    for (const key of legacyKeys) {
      try {
        const raw = localStorage.getItem(key)
        if (!raw) continue

        const tokenValue = extractStoredAuthToken(raw)

        if (tokenValue) {
          this._token.value = tokenValue
          this.clearRefreshSessionFields()
          this.persist()
          break
        }
      } catch {
        // ignore
      }
    }
  }

  /**
   * 从持久化重新同步 token，用于多窗口刷新后修正当前窗口内存态
   */
  syncTokenFromStorage(): string | null {
    let nextToken: string | null = null
    let nextUserInfo: UserInfo | null = null
    let nextRefreshToken: string | null = null
    let nextAccessTokenExpiresAt: number | null = null
    let nextRefreshTokenExpiresAt: number | null = null
    let nextSessionId: string | null = null
    let nextRefreshVersion: number | null = null

    try {
      const data = this.storage.getItem<string>(this.storageKey)
      if (data) {
        const state = JSON.parse(data)
        nextToken = extractStoredAuthToken(state) || null
        nextRefreshToken =
          typeof state?.refreshToken === 'string' && state.refreshToken.trim()
            ? state.refreshToken
            : null
        nextAccessTokenExpiresAt =
          typeof state?.accessTokenExpiresAt === 'number' ? state.accessTokenExpiresAt : null
        nextRefreshTokenExpiresAt =
          typeof state?.refreshTokenExpiresAt === 'number' ? state.refreshTokenExpiresAt : null
        nextSessionId =
          typeof state?.sessionId === 'string' && state.sessionId.trim() ? state.sessionId : null
        nextRefreshVersion =
          typeof state?.refreshVersion === 'number' ? state.refreshVersion : null
        nextUserInfo = state?.userInfo || null
      }
    } catch (error) {
      console.error('[AuthStore] Failed to sync token from storage', error)
    }

    const oldToken = this._token.value
    this._token.value = nextToken
    this._refreshToken.value = nextToken ? nextRefreshToken : null
    this._accessTokenExpiresAt.value = nextToken ? nextAccessTokenExpiresAt : null
    this._refreshTokenExpiresAt.value = nextToken ? nextRefreshTokenExpiresAt : null
    this._sessionId.value = nextToken ? nextSessionId : null
    this._refreshVersion.value = nextToken ? nextRefreshVersion : null
    this._userInfo.value = nextToken ? nextUserInfo : null

    if (oldToken !== nextToken) {
      this.notifyTokenChange(nextToken)
    }
    return nextToken
  }

  /**
   * 获取 token
   */
  get token(): string | null {
    return this._token.value
  }

  /** 获取标准 access token */
  get accessToken(): string | null {
    return this._token.value
  }

  /** 获取标准 refresh token */
  get refreshToken(): string | null {
    return this._refreshToken.value
  }

  /** 获取 access token 过期时间 */
  get accessTokenExpiresAt(): number | null {
    return this._accessTokenExpiresAt.value
  }

  /** 获取 refresh token 过期时间 */
  get refreshTokenExpiresAt(): number | null {
    return this._refreshTokenExpiresAt.value
  }

  /** 获取当前后端 sessionId */
  get sessionId(): string | null {
    return this._sessionId.value
  }

  /** 获取当前 refresh 版本 */
  get refreshVersion(): number | null {
    return this._refreshVersion.value
  }

  /** 获取当前标准双 token 会话 */
  getAuthSession(): AuthSession | null {
    if (!this._token.value || !this._refreshToken.value || !this._sessionId.value) return null
    return {
      accessToken: this._token.value,
      refreshToken: this._refreshToken.value,
      accessTokenExpiresAt: this._accessTokenExpiresAt.value || 0,
      refreshTokenExpiresAt: this._refreshTokenExpiresAt.value || 0,
      sessionId: this._sessionId.value,
      refreshVersion: this._refreshVersion.value || 0,
      userInfo: this._userInfo.value
    }
  }

  /**
   * 设置 token
   */
  set token(value: string | null) {
    const oldValue = this._token.value
    this._token.value = value
    if (!value) {
      this.clearRefreshSessionFields()
    }
    this.persist()

    // 触发监听器
    if (oldValue !== value) {
      this.notifyTokenChange(value)
    }
  }

  /**
   * 设置 token（方法形式，用于 SSR）
   */
  setToken(value: string | null): void {
    this.token = value
  }

  private clearRefreshSessionFields(): void {
    this._refreshToken.value = null
    this._accessTokenExpiresAt.value = null
    this._refreshTokenExpiresAt.value = null
    this._sessionId.value = null
    this._refreshVersion.value = null
  }

  /**
   * 监听 token 变化
   */
  onTokenChange(listener: (token: string | null) => void): () => void {
    this.tokenChangeListeners.push(listener)

    // 返回取消监听的函数
    return () => {
      const index = this.tokenChangeListeners.indexOf(listener)
      if (index > -1) {
        this.tokenChangeListeners.splice(index, 1)
      }
    }
  }

  /**
   * 通知 token 变化
   */
  private notifyTokenChange(token: string | null): void {
    this.tokenChangeListeners.forEach((listener) => {
      try {
        listener(token)
      } catch (error) {
        console.error('[AuthStore] Token change listener error:', error)
      }
    })
  }

  /**
   * 获取用户信息
   */
  get userInfo(): UserInfo | null {
    return this._userInfo.value
  }

  /**
   * 设置用户信息
   */
  set userInfo(value: UserInfo | null) {
    this._userInfo.value = value
    this.persist()
  }

  /**
   * 获取二维码信息
   */
  get qrCodeInfo(): QRCodeInfo | null {
    return this._qrCodeInfo.value
  }

  /**
   * 设置二维码信息
   */
  set qrCodeInfo(value: QRCodeInfo | null) {
    this._qrCodeInfo.value = value
    // 二维码信息不需要持久化
  }

  /**
   * 设置登录信息
   * @param token - 登录 token
   * @param userInfo - 用户信息，可为 null（扫码登录时可能不返回用户信息）
   */
  setLoginInfo(token: string, userInfo: UserInfo | null): void {
    const oldToken = this._token.value
    this._token.value = token
    this.clearRefreshSessionFields()
    this._userInfo.value = userInfo
    this.persist()

    // 触发监听器
    if (oldToken !== token) {
      this.notifyTokenChange(token)
    }
  }

  /**
   * 设置标准双 token 会话。
   * 只有后端明确返回 refresh token/session/version 时才进入可刷新会话。
   */
  setAuthSession(session: AuthSession): void {
    const oldToken = this._token.value
    this._token.value = session.accessToken
    this._refreshToken.value = session.refreshToken
    this._accessTokenExpiresAt.value = session.accessTokenExpiresAt
    this._refreshTokenExpiresAt.value = session.refreshTokenExpiresAt
    this._sessionId.value = session.sessionId
    this._refreshVersion.value = session.refreshVersion
    this._userInfo.value = session.userInfo
    this.persist()

    if (oldToken !== session.accessToken) {
      this.notifyTokenChange(session.accessToken)
    }
  }

  /**
   * 清除登录信息
   */
  clearLoginInfo(): void {
    const hadToken = !!this._token.value
    this._token.value = null
    this.clearRefreshSessionFields()
    this._userInfo.value = null
    this._qrCodeInfo.value = null

    // 清除所有认证相关的存储
    this.clear()

    // 额外清除可能存在的其他 token 相关 key
    try {
      this.storage.removeItem(STORAGE_KEYS.USER_INFO)
      this.storage.removeItem(STORAGE_KEYS.QR_CODE)

      // 如果是浏览器环境，清除通用 token key
      if (typeof window !== 'undefined' && window.localStorage) {
        // 只清除通用的 token key，不清除项目特定的 key
        // 项目特定的 key 应该由 storage 的前缀机制处理
        try {
          window.localStorage.removeItem('token')
        } catch (e) {
          // 忽略错误
        }

        console.log('[AuthStore] Cleared authentication data from localStorage')
      }
    } catch (error) {
      console.error('[AuthStore] Failed to clear additional storage items', error)
    }

    // 触发监听器
    if (hadToken) {
      this.notifyTokenChange(null)
    }
  }

  /**
   * 序列化状态
   */
  serialize(): string {
    const state = {
      token: this._token.value,
      accessToken: this._token.value,
      refreshToken: this._refreshToken.value,
      accessTokenExpiresAt: this._accessTokenExpiresAt.value,
      refreshTokenExpiresAt: this._refreshTokenExpiresAt.value,
      sessionId: this._sessionId.value,
      refreshVersion: this._refreshVersion.value,
      userInfo: this._userInfo.value
    }
    return JSON.stringify(state)
  }

  /**
   * 反序列化状态
   */
  deserialize(data: string): void {
    try {
      const state = JSON.parse(data)
      this._token.value = state.accessToken || state.token || null
      this._refreshToken.value = state.refreshToken || null
      this._accessTokenExpiresAt.value =
        typeof state.accessTokenExpiresAt === 'number' ? state.accessTokenExpiresAt : null
      this._refreshTokenExpiresAt.value =
        typeof state.refreshTokenExpiresAt === 'number' ? state.refreshTokenExpiresAt : null
      this._sessionId.value = state.sessionId || null
      this._refreshVersion.value =
        typeof state.refreshVersion === 'number' ? state.refreshVersion : null
      this._userInfo.value = this._token.value ? state.userInfo || null : null
      if (!this._token.value) {
        this.clearRefreshSessionFields()
      }
    } catch (error) {
      console.error('[AuthStore] Failed to deserialize state', error)
    }
  }

  /**
   * 获取当前状态
   */
  getState(): AuthState {
    return {
      token: this._token.value,
      accessToken: this._token.value,
      refreshToken: this._refreshToken.value,
      accessTokenExpiresAt: this._accessTokenExpiresAt.value,
      refreshTokenExpiresAt: this._refreshTokenExpiresAt.value,
      sessionId: this._sessionId.value,
      refreshVersion: this._refreshVersion.value,
      userInfo: this._userInfo.value,
      qrCodeInfo: this._qrCodeInfo.value,
      isLogin: this.isLogin.value
    }
  }
}
